import useImageRecovery from '../../../hooks/useImageRecovery';
import { getUrlDisplayTitle } from '../../../util/displayText';
import UrlPreviewReadButton from '../../UrlPreviewReadButton';
import '../../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useState, useRef, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import styled from 'styled-components';
import Hls from 'hls.js';
import VideoPosterPreview from './VideoPosterPreview';
import Duration from '../../Duration';
import CarouselModal from '../../Modals/CarouselModal';
import Markdown from 'markdown-to-jsx';

import useUpdateMessageMutation from '../../../hooks/useUpdateMessageMutation';

import { AuthContext } from '../../../util/context';
import environment from '../../../env-config';
import { Image, Transformation } from 'cloudinary-react';

import { IoIosClose, IoMdLink } from 'react-icons/io';
import { MdVerified } from 'react-icons/md';
import { FiBookOpen } from 'react-icons/fi';
import { VideoPlayOverlay } from './PlayIconOverlay';
import { useInViewVideoPlayback } from '../../../hooks/useInViewVideoPlayback';
import { setVideoPlaybackTime, getVideoPlaybackTime } from '../../../util/inViewVideoController';
import ReactPlayer from 'react-player';
import { getYouTubeVideoId } from '../../../util/youtubeVideoId';
import { GalleryPreviewGrid, GalleryPreviewItem } from './GalleryPreviewGrid';
import { buildWorkspaceMediaGallery } from '../../../util/workspaceMediaGallery';

import {
    PRIMARY_COLOR,
    WHITE,
    FILE_BLUE,
    PRIMARY_COLOR_4,
    PRIMARY_COLOR_2,
    PRIMARY_COLOR_1,
} from '../../../appearance/Colors';
import ImageNAIcon from '../../../appearance/ImageNAIcon';

import domain_from_url from "../../../util/domain-from-url";
import { getEngagementRateBadge } from '../../../util/engagement-rate-badge';
import customAnalytics from '../../../util/custom-analytics';
import { LAYOUT } from '../layoutBreakpoints';
import { getMasonryVideoMuted, setMasonryVideoMuted, subscribeMasonryVideoMuted } from '../../../util/masonryVideoMutePreference';
import { GET_MESSAGE } from '../../../gql-operations/queries/message/get-message';
import UrlMetaQuotedPreview from '../../UrlMetaQuotedPreview';
import {
    buildSearchMatchPreviewExcerpt,
    renderHighlightedSearchText
} from '../../../util/searchHighlight';
import ArticleReaderModal, { getReadableArticleContent } from '../../ArticleReaderModal';
import TextToSpeechCreateModal from '../../TextToSpeechCreateModal';
import {
    decodeProviderMediaUrl,
    getUniqueMediaSources,
    getNextMediaSource,
    getUrlMetaDirectS3VideoSource,
    getUrlMetaOriginalVideoUrl,
    getUrlMetaVideoSource,
    resolveUrlMetaPreviewMedia,
    normalizeMediaUrl,
    isUrlMetaVideo
} from '../../../util/url-meta-media';
import {
    hasUrlPreviewMarkdownFormatting,
    normalizeUrlPreviewText,
    truncateUrlPreviewText,
    URL_PREVIEW_DESCRIPTION_COLLAPSE_LIMIT,
    URL_PREVIEW_TITLE_COLLAPSE_LIMIT
} from '../../../util/urlPreviewText';

const logVideoWorkspace = () => undefined;
const URL_META_DESCRIPTION_VISIBLE_WINDOW = 180;
const URL_META_DESCRIPTION_EXCERPT_LENGTH = 180;

// Separate Audio Component
const RedditAudioPlayer = React.forwardRef(({ audioUrl, onPlay, onPause, onSeek, onVolumeChange, onTimeUpdate }, ref) => {
    const audioRef = React.useRef(null);

    // no-op

    React.useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handlePlay = () => onPlay && onPlay();
        const handlePause = () => onPause && onPause();
        const handleTimeUpdate = () => onTimeUpdate && onTimeUpdate(audio.currentTime);
        const handleSeek = () => onSeek && onSeek(audio.currentTime);
        const handleVolumeChange = () => onVolumeChange && onVolumeChange(audio.volume);

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('seeked', handleSeek);
        audio.addEventListener('volumechange', handleVolumeChange);

        return () => {
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('seeked', handleSeek);
            audio.removeEventListener('volumechange', handleVolumeChange);
        };
    }, [onPlay, onPause, onSeek, onVolumeChange, onTimeUpdate]);

    const play = () => audioRef.current?.play();
    const pause = () => audioRef.current?.pause();
    const seek = (time) => {
        if (audioRef.current) audioRef.current.currentTime = time;
    };
    const setVolume = (volume) => {
        if (audioRef.current) audioRef.current.volume = volume;
    };

    // Expose methods to parent via ref
    React.useImperativeHandle(ref, () => ({
        play,
        pause,
        seek,
        setVolume,
        currentTime: audioRef.current?.currentTime || 0,
        duration: audioRef.current?.duration || 0
    }));

    return (
        <audio
            ref={audioRef}
            src={audioUrl}
            preload="metadata"
            referrerPolicy="no-referrer"
            style={{ display: 'none' }}
        />
    );
});

RedditAudioPlayer.displayName = 'RedditAudioPlayer';

// Native video controls render inside the bottom strip of the element; clicking
// there should never be treated as a "toggle play/pause" body click, or it
// double-toggles whatever the native control button itself just did.
const CONTROL_BAR_HEIGHT_PX = 40;

// Separate Video Component
const RedditVideoPlayer = ({
    videoUrl,
    audioUrl,
    poster,
    width,
    height,
    hasAudio,
    hlsUrl,
    fillFrame = false,
    autoplayOnHover = false,
    showInlinePlayer = false,
    isYouTube = false,
    onOpenTranscript,
    onLoad,
    onError,
    messageId
}) => {
    const {t: lnT} = useTranslation();

    // Derive audio URL for Reddit DASH when not provided
    const computedAudioUrl = React.useMemo(() => {
        if (audioUrl && audioUrl.length > 0) return audioUrl;
        if (videoUrl && videoUrl.includes('v.redd.it')) {
            try {
                const url = new URL(videoUrl);
                const parts = url.pathname.split('/').filter(Boolean);
                // Expect: /<id>/DASH_480.mp4 or similar
                if (parts.length >= 2) {
                    const base = `${url.protocol}//${url.host}/${parts[0]}`;
                    return `${base}/DASH_AUDIO_128.mp4`;
                }
            } catch {}
        }
        return '';
    }, [lnT, videoUrl, audioUrl]);
    const videoRef = React.useRef(null);
    const audioRef = React.useRef(null);
    // Only split Reddit DASH media needs a separate audio element. Instagram's
    // MP4 includes its own audio track, even when media_has_audio is true.
    // In masonry (autoplayOnHover tiles), the mute preference is shared across
    // every video on the page: unmuting one unmutes all the others live (not just
    // future ones), and vice versa. Outside masonry, default mute follows the old
    // mobile-viewport heuristic.
    const shouldDefaultToMuted = autoplayOnHover
        ? getMasonryVideoMuted()
        : (
            typeof window === 'undefined'
                ? true
                : window.innerWidth <= LAYOUT.MOBILE_VIEWPORT_MAX
        );
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [currentTime, setCurrentTime] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    const [volume, setVolume] = React.useState(1);
    const [showControls, setShowControls] = React.useState(false);
    const [failedHls, setFailedHls] = React.useState(false);
    const useFallbackAudio = (!hlsUrl || failedHls) && hasAudio !== false && !!computedAudioUrl;
    const [isHovered, setIsHovered] = React.useState(false);

    // Direct playable video: HLS, direct video file (.mp4, .webm), or YouTube.
    // Instagram/TikTok webpage URLs cannot play in HTML <video> or ReactPlayer directly.
    const isDirectPlayableVideo = Boolean(
        hlsUrl ||
        isYouTube ||
        getYouTubeVideoId(videoUrl) ||
        (/\.(mp4|webm|m4v|ogv|m3u8)(?:[?#]|$)/i.test(String(videoUrl || ''))) ||
        (videoUrl && videoUrl.includes('luckynote.s3')) ||
        (videoUrl && videoUrl.includes('v.redd.it'))
    );

    const rawPlaybackId = isDirectPlayableVideo ? (videoUrl || hlsUrl || '') : '';
    const videoPlaybackId = rawPlaybackId ? (messageId ? `${messageId}-${rawPlaybackId}` : rawPlaybackId) : '';
    const {
        elementRef: inViewElementRef,
        shouldPlay: inViewShouldPlay,
        handleMouseEnter,
        handleMouseLeave
    } = useInViewVideoPlayback({
        id: videoPlaybackId,
        enabled: Boolean(autoplayOnHover && isDirectPlayableVideo && !showInlinePlayer)
    });

    const handlePlayerMouseEnter = React.useCallback(event => {
        setIsHovered(true);
        handleMouseEnter?.(event);
    }, [handleMouseEnter]);

    const handlePlayerMouseLeave = React.useCallback(event => {
        setIsHovered(false);
        handleMouseLeave?.(event);
    }, [handleMouseLeave]);

    React.useEffect(() => setFailedHls(false), [videoUrl, hlsUrl]);
    const [isMuted, setIsMuted] = React.useState(shouldDefaultToMuted);

    React.useEffect(() => {
        setIsMuted(shouldDefaultToMuted);
    }, [shouldDefaultToMuted, videoUrl, hlsUrl]);

    React.useEffect(() => {
        if (videoRef.current) {
            videoRef.current.style.display = '';
        }
    }, [videoUrl, hlsUrl, failedHls]);

    React.useEffect(() => {
        if (!autoplayOnHover) {
            return undefined;
        }

        return subscribeMasonryVideoMuted(nextMuted => setIsMuted(nextMuted));
    }, [autoplayOnHover]);

    // Native fullscreen expands the exact same <video> element, which normally has
    // object-fit: cover for card/tile display — force contain while fullscreen so a
    // portrait/mobile-shaped video is letterboxed instead of cropped/stretched.
    React.useEffect(() => {
        const video = videoRef.current;
        if (!video) {
            return undefined;
        }

        const handleFullscreenChange = () => {
            const fullscreenElement = document.fullscreenElement
                || document.webkitFullscreenElement
                || document.mozFullScreenElement;
            video.style.objectFit = fullscreenElement === video ? 'contain' : 'cover';
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
        };
    }, [videoUrl, hlsUrl]);

    React.useEffect(() => {
        const video = videoRef.current;
        if (!video || !autoplayOnHover) {
            return undefined;
        }

        const handleVolumePreferenceChange = () => {
            const nextMuted = Boolean(video.muted || video.volume === 0);
            setMasonryVideoMuted(nextMuted);
        };

        video.addEventListener('volumechange', handleVolumePreferenceChange);
        return () => {
            video.removeEventListener('volumechange', handleVolumePreferenceChange);
        };
    }, [autoplayOnHover, videoUrl, hlsUrl]);

    React.useEffect(() => {
        const video = videoRef.current;
        if (!video || !autoplayOnHover || typeof document === 'undefined') {
            return undefined;
        }

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                return;
            }

            video.pause();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [autoplayOnHover, videoUrl, hlsUrl]);

    // Setup HLS if available
    React.useEffect(() => {
        const video = videoRef.current;
        if (!video || !hlsUrl || failedHls || !inViewShouldPlay) return;

        if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = hlsUrl;
            const playPromise = video.play();
            if (playPromise?.catch) playPromise.catch(() => {});
            return;
        }

        if (Hls.isSupported()) {
            const hls = new Hls({ 
                enableWorker: true,
                xhrSetup: function (xhr, url) {
                    xhr.withCredentials = false;
                    // Attempt to strip referrer from segments/manifest
                    if (xhr.setRequestHeader) {
                        // no-referrer is usually handled by browser, but we ensure credentials are off
                    }
                }
            });

            hls.on(Hls.Events.ERROR, function (event, data) {
                if (data.fatal) {
                    console.warn('HLS fatal error, falling back to MP4:', data);
                    setFailedHls(true);
                    hls.destroy();
                }
            });

            hls.on(Hls.Events.MANIFEST_PARSED, function () {
                const playPromise = video.play();
                if (playPromise?.catch) playPromise.catch(() => {});
            });

            hls.loadSource(hlsUrl);
            hls.attachMedia(video);
            return () => {
                try { hls.destroy(); } catch {}
            };
        }
    }, [hlsUrl, failedHls, inViewShouldPlay]);

    React.useEffect(() => {
        const video = videoRef.current;
        if (!video || !inViewShouldPlay || (hlsUrl && !failedHls)) return;
        video.muted = true;
        const playPromise = video.play();
        if (playPromise?.catch) {
            playPromise.catch(() => {});
        }
    }, [inViewShouldPlay, videoUrl, hlsUrl, failedHls]);

    // no-op

    // Audio event handlers
    const handleAudioPlay = () => {
        console.log('Audio started playing');
    };

    const handleAudioPause = () => {
        console.log('Audio paused');
    };

    const handleAudioTimeUpdate = (audioTime) => {
        // Sync video with audio if they're out of sync
        if (videoRef.current && Math.abs(videoRef.current.currentTime - audioTime) > 0.1) {
            videoRef.current.currentTime = audioTime;
        }
    };

    const handleAudioSeek = (audioTime) => {
        if (videoRef.current) {
            videoRef.current.currentTime = audioTime;
        }
    };

    const handleAudioVolumeChange = (audioVolume) => {
        if (videoRef.current) {
            videoRef.current.volume = audioVolume;
        }
        setVolume(audioVolume);
    };

    // Video event handlers
    const handleVideoTimeUpdate = event => {
        const video = event?.currentTarget || videoRef.current;
        if (!video) {
            return;
        }

        const cur = video.currentTime || 0;
        setCurrentTime(cur);
        setVideoPlaybackTime(videoPlaybackId, cur);
        // Sync audio with video if they're out of sync
        if ((hasAudio || computedAudioUrl) && audioRef.current && Math.abs(audioRef.current.currentTime - video.currentTime) > 0.1) {
            audioRef.current.currentTime = video.currentTime;
        }
    };

    const handleVideoPlay = event => {
        const video = event?.currentTarget || videoRef.current;
        setIsPlaying(true);

        // Start audio playback when video starts (only when no HLS)
        if (video && useFallbackAudio && audioRef.current) {
            audioRef.current.currentTime = video.currentTime;
            audioRef.current.play().catch((error) => {
                console.log('Audio play error:', error);
            });
        }
    };

    const handleVideoPause = () => {
        setIsPlaying(false);
        // Pause audio when video pauses (only when no HLS)
        if (useFallbackAudio && audioRef.current) {
            audioRef.current.pause();
        }
    };

    const handleVideoSeek = event => {
        const video = event?.currentTarget || videoRef.current;
        // Sync audio seek with video (only when no HLS)
        if (video && useFallbackAudio && audioRef.current) {
            audioRef.current.currentTime = video.currentTime;
        }
    };

    const handleVideoVolumeChange = event => {
        const video = event?.currentTarget || videoRef.current;
        if (!video) {
            return;
        }

        // Sync audio volume with video (only when no HLS)
        if (useFallbackAudio && audioRef.current) {
            audioRef.current.volume = video.volume;
        }
        setVolume(video.volume);
    };

    const handleVideoLoadedMetadata = event => {
        const video = event?.currentTarget || videoRef.current;
        if (!video) {
            return;
        }

        setDuration(video.duration);
    };

    // Setup video event listeners
    React.useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        video.addEventListener('play', handleVideoPlay);
        video.addEventListener('pause', handleVideoPause);
        video.addEventListener('timeupdate', handleVideoTimeUpdate);
        video.addEventListener('seeked', handleVideoSeek);
        video.addEventListener('volumechange', handleVideoVolumeChange);
        video.addEventListener('loadedmetadata', handleVideoLoadedMetadata);

        return () => {
            video.removeEventListener('play', handleVideoPlay);
            video.removeEventListener('pause', handleVideoPause);
            video.removeEventListener('timeupdate', handleVideoTimeUpdate);
            video.removeEventListener('seeked', handleVideoSeek);
            video.removeEventListener('volumechange', handleVideoVolumeChange);
            video.removeEventListener('loadedmetadata', handleVideoLoadedMetadata);
        };
    }, [hasAudio, audioUrl, computedAudioUrl]);

    const handleVideoClick = event => {
        event.stopPropagation();

        if (!videoRef.current) {
            return;
        }

        // Native video controls render inside the bottom strip of the element;
        // clicking there should never be treated as a "toggle play/pause" body
        // click, or it double-toggles whatever the native control button itself
        // just did (this is how "click breaks play/pause" regressed before).
        const rect = event.currentTarget.getBoundingClientRect();
        const clickedControlBar = event.clientY >= rect.bottom - CONTROL_BAR_HEIGHT_PX;
        if (clickedControlBar) {
            return;
        }

        if (autoplayOnHover && videoRef.current.muted) {
            videoRef.current.muted = false;
            if (useFallbackAudio && audioRef.current) {
                audioRef.current.volume = videoRef.current.volume || 1;
            }
            setMasonryVideoMuted(false);
            if (videoRef.current.paused) {
                videoRef.current.play().catch(error => {
                    console.debug('[URLPreviewSection] Click-to-unmute autoplay was blocked:', error);
                });
            }
            return;
        }
    };

    const handleSeek = (e) => {
        if (!videoRef.current) {
            return;
        }

        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const newTime = (clickX / rect.width) * duration;
        videoRef.current.currentTime = newTime;
        if (hasAudio && audioUrl && audioRef.current) {
            audioRef.current.currentTime = newTime;
        }
    };

    const handleVolumeChange = (e) => {
        if (!videoRef.current) {
            return;
        }

        const newVolume = parseFloat(e.target.value);
        videoRef.current.volume = newVolume;
        if (hasAudio && audioUrl && audioRef.current) {
            audioRef.current.volume = newVolume;
        }
        setVolume(newVolume);
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    if (!showInlinePlayer) {
        const canPlayNow = Boolean(inViewShouldPlay && isDirectPlayableVideo);
        const isPlayerSupported = Boolean(
            isYouTube ||
            getYouTubeVideoId(videoUrl) ||
            (videoUrl && typeof ReactPlayer.canPlay === 'function' && ReactPlayer.canPlay(videoUrl))
        );

        return (
            <div
                ref={inViewElementRef}
                onMouseEnter={handlePlayerMouseEnter}
                onMouseLeave={handlePlayerMouseLeave}
                style={fillFrame
                    ? { position: 'absolute', inset: 0, width: '100%', height: '100%', cursor: 'pointer' }
                    : { position: 'relative', cursor: 'pointer' }}
            >
                {canPlayNow ? (
                    isPlayerSupported ? (
                        <div style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'hidden', borderRadius: 8 }}>
                            <ReactPlayer
                                url={videoUrl}
                                width="100%"
                                height="100%"
                                playing={true}
                                muted={true}
                                loop={true}
                                playsinline={true}
                                onDuration={d => setDuration(d || 0)}
                                onProgress={({ playedSeconds: p }) => {
                                    const cur = p || 0;
                                    setCurrentTime(cur);
                                    setVideoPlaybackTime(videoPlaybackId, cur);
                                }}
                                config={{
                                    file: {
                                        attributes: {
                                            referrerPolicy: 'no-referrer',
                                            playsInline: true,
                                            style: { width: '100%', height: '100%', objectFit: 'cover' }
                                        }
                                    },
                                    youtube: {
                                        playerVars: {
                                            autoplay: 1,
                                            mute: 1,
                                            controls: 0,
                                            modestbranding: 1,
                                            rel: 0,
                                            showinfo: 0,
                                            iv_load_policy: 3
                                        }
                                    }
                                }}
                            />
                        </div>
                    ) : (
                        <video
                            ref={videoRef}
                            src={(hlsUrl && !failedHls) ? undefined : videoUrl}
                            referrerPolicy="no-referrer"
                            autoPlay
                            muted
                            playsInline
                            loop
                            poster={poster}
                            onTimeUpdate={handleVideoTimeUpdate}
                            onLoadedMetadata={handleVideoLoadedMetadata}
                            style={{
                                borderRadius: 8,
                                maxHeight: fillFrame ? 'none' : 350,
                                objectFit: 'cover',
                                width: '100%',
                                height: '100%',
                                backgroundColor: '#000',
                                display: 'block'
                            }}
                        />
                    )
                ) : (
                    <>
                        <PreviewPlaceholder isVisible={!poster && !videoUrl && !hlsUrl} />
                        <VideoPosterPreview
                            poster={poster}
                            videoUrl={videoUrl || hlsUrl}
                            alt={lnT('messages:videoPreview')}
                            onLoad={onLoad}
                            onError={onError}
                        />
                        <VideoPlayOverlay isHovered={isHovered} />
                    </>
                )}
                {canPlayNow && duration > 0 ? (
                    <span
                        data-testid="social-video-time-left"
                        style={{
                            position: 'absolute',
                            bottom: 8,
                            left: 8,
                            zIndex: 4,
                            padding: '2px 6px',
                            borderRadius: 4,
                            backgroundColor: 'rgba(0, 0, 0, 0.65)',
                            backdropFilter: 'blur(4px)',
                            WebkitBackdropFilter: 'blur(4px)',
                            color: '#fff',
                            fontSize: 11,
                            fontWeight: 500,
                            lineHeight: '14px',
                            pointerEvents: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            letterSpacing: '0.02em',
                            fontVariantNumeric: 'tabular-nums'
                        }}
                    >
                        <Duration seconds={Math.max(0, Math.ceil(duration - currentTime))} />
                    </span>
                ) : null}
                {typeof onOpenTranscript === 'function' ? (
                    <TranscriptCornerButton
                        type="button"
                        aria-label={lnT('messages:openTranscriptWorkspace')}
                        title={lnT('messages:transcript')}
                        onClick={event => {
                            event.preventDefault();
                            event.stopPropagation();
                            onOpenTranscript();
                        }}
                    >{lnT('messages:cc')}</TranscriptCornerButton>
                ) : null}
            </div>
        );
    }

    return (
        <div 
            style={fillFrame
                ? { position: 'absolute', inset: 0, width: '100%', height: '100%' }
                : { position: 'relative' }}
            onClick={event => event.stopPropagation()}
        >
            <video
                ref={videoRef}
                src={(hlsUrl && !failedHls) ? undefined : videoUrl}
                style={{
                    borderRadius: 8,
                    maxHeight: fillFrame ? 'none' : 350,
                    objectFit: 'cover',
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#000',
                    cursor: 'pointer',
                    display: 'block'
                }}
                controls
                referrerPolicy="no-referrer"
                poster={poster}
                width={width}
                height={height}
                muted={isMuted}
                onClick={handleVideoClick}
                onError={(e) => {
                    if (hlsUrl && !failedHls) {
                        setFailedHls(true);
                        return;
                    }
                    const currentUrl = e.target.src || '';
                    // Try alternative Reddit video URL if available
                    if (currentUrl.includes('v.redd.it')) {
                        if (currentUrl.includes('DASH_')) {
                            const newHlsUrl = currentUrl.replace('DASH_', 'HLSPlaylist.m3u8').replace('.mp4', '');
                            e.target.src = newHlsUrl;
                        } else if (currentUrl.includes('HLSPlaylist')) {
                            const dashUrl = currentUrl.replace('HLSPlaylist.m3u8', 'DASH_480.mp4');
                            e.target.src = dashUrl;
                        } else {
                            onError?.(currentUrl);
                        }
                    } else if (currentUrl.includes('twimg.com')) {
                        // If it's a Twitter video and it failed (e.g., 403), try to fall back or hide
                        console.warn('X Video failed to load:', currentUrl);
                        // If we have an HLS URL but we hit the MP4, it's already handled by RedditVideoPlayer logic
                        // but if we are here, something else broke.
                        onError?.(currentUrl);
                    } else {
                        if (hlsUrl && !failedHls) {
                            setFailedHls(true);
                            return;
                        }
                        onError?.(currentUrl);
                    }
                }}
                onLoadedMetadata={onLoad}
                onCanPlay={onLoad}
            />

            {/* Hidden Audio Component (only when no HLS) */}
            {useFallbackAudio && (
                <RedditAudioPlayer
                    ref={audioRef}
                    audioUrl={computedAudioUrl}
                    onPlay={handleAudioPlay}
                    onPause={handleAudioPause}
                    onSeek={handleAudioSeek}
                    onVolumeChange={handleAudioVolumeChange}
                    onTimeUpdate={handleAudioTimeUpdate}
                />
            )}

            {/* Audio Indicator */}
            {hasAudio === false && (
                <div style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: 4,
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                }}>{lnT('messages:noAudio')}</div>
            )}
        </div>
    );
};

const toStableMediaIdentity = source => {
    const value = String(source || '').trim();
    if (!value) return '';

    try {
        const parsed = new URL(value);
        return `${parsed.origin}${parsed.pathname}`;
    } catch (error) {
        return value.split('?')[0].split('#')[0];
    }
};

const buildMetaMediaIdentity = meta => {
    if (!meta) return 'none';

    const galleryImages = Array.isArray(meta.gallery_images)
        ? meta.gallery_images.map(toStableMediaIdentity).join(',')
        : '';
    const gallery = Array.isArray(meta.gallery)
        ? meta.gallery.map(slide => [
            slide?.type || '',
            toStableMediaIdentity(slide?.image),
            toStableMediaIdentity(slide?.media_cdn_url),
            toStableMediaIdentity(slide?.media_url),
            slide?.image_name || '',
            slide?.media_name || ''
        ].join(':')).join(',')
        : '';

    return [
        meta.url || '',
        meta.media_type || '',
        toStableMediaIdentity(meta.media_cdn_url),
        toStableMediaIdentity(meta.media_url),
        toStableMediaIdentity(meta.media_hls_url),
        galleryImages,
        gallery,
        toStableMediaIdentity(meta.image_cdn_url),
        toStableMediaIdentity(meta.image),
        meta.public_id || '',
        toStableMediaIdentity(meta.thumbnail?.lg),
        toStableMediaIdentity(meta.thumbnail?.xl),
        toStableMediaIdentity(meta.thumbnail?.md),
        toStableMediaIdentity(meta.thumbnail?.original),
        toStableMediaIdentity(meta.author_image_cdn_url),
        toStableMediaIdentity(meta.author_image),
        toStableMediaIdentity(meta.author_image_original),
        meta.author_image_public_id || '',
        toStableMediaIdentity(meta.author_image_thumbnail?.lg),
        toStableMediaIdentity(meta.author_image_thumbnail?.xl),
        toStableMediaIdentity(meta.author_image_thumbnail?.md),
        toStableMediaIdentity(meta.author_image_thumbnail?.original)
    ].join('|');
};

const isYouTubeUrl = value => {
    try {
        const rawUrl = String(value || '').trim();
        const host = new URL(
            /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`
        ).hostname.toLowerCase();
        return host === 'youtube.com' || host === 'www.youtube.com' || host === 'm.youtube.com' ||
            host === 'music.youtube.com' || host === 'youtu.be';
    } catch (error) {
        return false;
    }
};

const isTranscriptSupportedVideoMeta = meta => {
    const rawUrl = String(meta?.url || meta?.media_url || meta?.media_cdn_url || '').trim();
    if (!rawUrl) return false;
    const hasTranscribableVideoMeta = Boolean(
        (meta?.media_url || meta?.media_cdn_url) && meta?.media_has_audio !== false
    ) || (Array.isArray(meta?.gallery) && meta.gallery.some(slide => (
        String(slide?.type || '').toLowerCase() === 'video' &&
        (slide?.media_url || slide?.media_cdn_url || slide?.media_hls_url || slide?.media?.url || slide?.media?.cdn_url) &&
        slide?.media_has_audio !== false
    )));

    try {
        const parsedUrl = new URL(/^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`);
        const host = parsedUrl.hostname.toLowerCase();
        if (/^(?:www\.)?threads\.(?:com|net)$/.test(host)) {
            return hasTranscribableVideoMeta;
        }
        if (
            host === 'instagram.com' ||
            host === 'www.instagram.com' ||
            host === 'instagr.am' ||
            host === 'www.instagr.am'
        ) {
            return String(meta?.media_type || '').toLowerCase() !== 'carousel' || hasTranscribableVideoMeta;
        }
        if (
            host === 'youtube.com' ||
            host === 'www.youtube.com' ||
            host === 'm.youtube.com' ||
            host === 'music.youtube.com' ||
            host === 'youtu.be' ||
            host === 'video.twimg.com'
        ) {
            return true;
        }
        if (
            host === 'tiktok.com' ||
            host === 'www.tiktok.com' ||
            host === 'm.tiktok.com' ||
            host === 'vt.tiktok.com'
        ) {
            const path = parsedUrl.pathname.toLowerCase();
            return !path.includes('/photo/') && !path.includes('/@photo/');
        }
        if (
            host === 'x.com' ||
            host === 'www.x.com' ||
            host === 'mobile.x.com' ||
            host === 'twitter.com' ||
            host === 'www.twitter.com' ||
            host === 'mobile.twitter.com'
        ) {
            return Boolean(
                hasTranscribableVideoMeta ||
                String(meta?.media_type || '').toLowerCase() === 'video'
            );
        }
    } catch {
        return false;
    }

    return false;
};

const buildUrlPreviewIdentity = meta => {
    if (!meta) return 'none';

    return [
        meta._id || '',
        buildMetaMediaIdentity(meta),
        meta.title || '',
        meta.description || '',
        meta.publisher || '',
        meta.author || '',
        meta.logo || '',
        meta.date || '',
        meta.article_body || '',
        meta.embed || '',
        meta.aspect_ratio || '',
        meta.media_width || '',
        meta.media_height || '',
        meta.media_duration || ''
    ].join('|');
};

const formatCompactMetric = value => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) return null;
    if (num >= 1_000_000) {
        const formatted = (num / 1_000_000).toFixed(1);
        return `${formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted}M`;
    }
    if (num >= 1_000) {
        const formatted = (num / 1_000).toFixed(1);
        return `${formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted}K`;
    }
    return String(num);
};

function URLPreviewSection({
    readOnly = false,
    index,
    metaData,
    message,
    mediaMaxWidth = 240,
    stackLayoutMaxWidth = LAYOUT.PREVIEW_STACK_MAX,
    layoutMode = 'list',
    showTitle = true,
    showDescription = true,
    searchHighlightTerm = '',
    onOpenVideoWorkspace,
    onCreateArticleAudio,
    isCreatingArticleAudio = false,
    autoplayOnHover = true
}) {
    const {t: lnT} = useTranslation();

    const [lightboxIsOpen, setLightboxIsOpen] = useState(false);
    const [isArticleReaderOpen, setIsArticleReaderOpen] = useState(false);
    const [quotedArticleReaderMeta, setQuotedArticleReaderMeta] = useState(null);
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [hasMediaLoaded, setHasMediaLoaded] = useState(false);
    const [loadedImageGeometry, setLoadedImageGeometry] = useState(null);
    const [hasMediaError, setHasMediaError] = useState(false);
    const [resolvedMetaData, setResolvedMetaData] = useState(metaData);
    const [currentMediaSrc, setCurrentMediaSrc] = useState(null);
    const [isRefreshingMedia, setIsRefreshingMedia] = useState(false);
    const [hasTriedMediaRefetch, setHasTriedMediaRefetch] = useState(false);
    const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
    const [articleAudioText, setArticleAudioText] = useState('');
    const [isArticleAudioModalOpen, setIsArticleAudioModalOpen] = useState(false);
    const wrapperRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const previousIndexRef = useRef(index);
    const previousMediaIdentityRef = useRef(buildMetaMediaIdentity(metaData));
    const previewImageRef = useRef(null);
    const latestImageSourceRef = useRef(null);
    const lastMeasuredWidthRef = useRef(0);
    const [measuredWidth, setMeasuredWidth] = useState(0);

    const {
        state: { holdingKeys, keys },
        context: { setSelectedMessage, setSearchTerm, setSearchActive, setSelectedUsername, setShowSocialModal }
    } = React.useContext(AuthContext);
    const { updateURL } = useUpdateMessageMutation();

    const handleCreateArticleAudio = React.useCallback(articleText => {
        if (readOnly) return;
        if (typeof onCreateArticleAudio === 'function') {
            onCreateArticleAudio({ message, urlMetaIndex: index, articleText });
            return;
        }

        setArticleAudioText(articleText || '');
        setIsArticleAudioModalOpen(true);
    }, [readOnly, lnT, index, message, onCreateArticleAudio]);
    const isMasonryLayout = layoutMode === 'masonry';
    const { aspect_ratio: storedAspectRatio } = message.urlMeta[index] || {};
    const currentMetaData = resolvedMetaData || metaData;
    const hasTranscriptForUrl = Boolean(
        message?.transcript?.text && (
            message.transcript.urlMetaIndex == null ||
            Number(message.transcript.urlMetaIndex) === Number(index)
        )
    );
    const articleBody = String(currentMetaData?.article_body || '').trim();
    const readableArticleBody = React.useMemo(
        () => getReadableArticleContent(articleBody, currentMetaData?.title),
        [lnT, articleBody, currentMetaData?.title]
    );
    const hasArticleBody = readableArticleBody.length > 0;
    const openArticleWorkspace = React.useCallback(articleMeta => {
        const article = articleMeta || currentMetaData;
        if (typeof onOpenVideoWorkspace !== 'function') {
            if (articleMeta) setQuotedArticleReaderMeta(articleMeta);
            else setIsArticleReaderOpen(true);
            return;
        }

        onOpenVideoWorkspace({
            type: 'article',
            title: article?.title || message?.title,
            url: article?.article_url || article?.url,
            articleBody: article?.article_body || articleBody,
            redditComments: article?.reddit_comments,
            sourcePlatform: article?.sourcePlatform,
            sourceMetrics: article?.sourceMetrics,
            sourcePlatformMeta: article?.sourcePlatformMeta,
            onCreateAudio: articleMeta ? undefined : handleCreateArticleAudio,
            isCreateAudioDisabled: isCreatingArticleAudio,
            isCreatingAudio: isCreatingArticleAudio
        });
    }, [lnT, articleBody, currentMetaData, handleCreateArticleAudio, isCreatingArticleAudio, message?.title, onOpenVideoWorkspace]);
    const gallerySlides = React.useMemo(() => {
        if (Array.isArray(currentMetaData?.gallery) && currentMetaData.gallery.length > 0) {
            const filteredGallery = currentMetaData.gallery.filter(
                slide => slide?.image || slide?.media_cdn_url || slide?.media_url || slide?.media_hls_url
            );

            // Single-item video entries (e.g. TikTok) store their video fields at the top level of
            // currentMetaData and only populate gallery with an image-type thumbnail slide (no
            // slide actually carries video fields). Returning that thumbnail slide here would null
            // out the real video fields below in activeMediaData, so treat this the same as an
            // empty gallery and let activeMediaData fall back to currentMetaData directly.
            const hasVideoSlide = filteredGallery.some(
                slide => slide?.type === 'video' || slide?.media_cdn_url || slide?.media_url || slide?.media_hls_url
            );
            if (!hasVideoSlide && isUrlMetaVideo(currentMetaData)) {
                return [];
            }

            return filteredGallery;
        }

        // Single-item video entries (e.g. TikTok) store their video fields at the top level of
        // currentMetaData and only populate gallery_images with a thumbnail. Synthesizing an
        // 'image' slide from that thumbnail would null out the real video fields below in
        // activeMediaData, so skip gallery synthesis entirely and let activeMediaData fall back
        // to currentMetaData directly.
        if (isUrlMetaVideo(currentMetaData)) {
            return [];
        }

        return getUniqueMediaSources(currentMetaData?.gallery_images || []).map(image => ({
            type: 'image',
            image,
            media_url: null
        }));
    }, [lnT, currentMetaData?.gallery, currentMetaData?.gallery_images, currentMetaData]);
    const workspaceMediaGallery = React.useMemo(
        () => buildWorkspaceMediaGallery(gallerySlides, {
            originalUrl: currentMetaData?.url || '',
            title: currentMetaData?.title || message?.title || ''
        }),
        [gallerySlides, currentMetaData?.url, currentMetaData?.title, message?.title]
    );
    const hasGalleryItems = gallerySlides.length > 1;
    const imageGallerySlides = gallerySlides
        .filter(slide => String(slide?.type || slide?.media?.type || '').trim().toLowerCase() !== 'video');
    const galleryOcrIsComplete = imageGallerySlides.length > 0 && imageGallerySlides
        .every(slide => (
            slide?.media?.ocrExtracted === true || String(slide?.media?.ocrText || slide?.ocrText || '').trim()
        ));
    const activeGallerySlide = gallerySlides[currentGalleryIndex] || null;
    const stableGalleryFrameSlide = hasGalleryItems
        ? (gallerySlides[0] || null)
        : activeGallerySlide;
    const activeMediaData = React.useMemo(() => {
        if (!activeGallerySlide) {
            return currentMetaData;
        }

        return {
            ...currentMetaData,
            image: activeGallerySlide.image || currentMetaData.image,
            image_cdn_url: null,
            thumbnail: null,
            media_cdn_url: activeGallerySlide.type === 'video' ? activeGallerySlide.media_cdn_url : null,
            media_url: activeGallerySlide.type === 'video' ? activeGallerySlide.media_url : null,
            media_hls_url: activeGallerySlide.type === 'video' ? activeGallerySlide.media_hls_url : null,
            media_type: activeGallerySlide.type,
            media_width: activeGallerySlide.width,
            media_height: activeGallerySlide.height,
            media_duration: activeGallerySlide.media_duration,
            media_has_audio: activeGallerySlide.media_has_audio
        };
    }, [lnT, activeGallerySlide, currentMetaData]);
    const activeS3VideoSource = React.useMemo(
        () => getUrlMetaDirectS3VideoSource(activeMediaData) || getUrlMetaDirectS3VideoSource(currentMetaData),
        [lnT, activeMediaData, currentMetaData]
    );
    // Hosted URL-preview videos use S3 directly: Bunny's pull-zone origin is
    // currently denied access to shared-media/video objects. Keep the existing
    // CDN/HLS resolution for external-provider videos.
    const activeVideoSource = React.useMemo(
        () => getUrlMetaVideoSource(activeMediaData) || getUrlMetaVideoSource(currentMetaData),
        [lnT, activeMediaData, activeS3VideoSource, currentMetaData]
    );
    const originalVideoSource = React.useMemo(
        () => getUrlMetaOriginalVideoUrl(activeMediaData) || getUrlMetaOriginalVideoUrl(currentMetaData),
        [lnT, activeMediaData, currentMetaData]
    );
    const normalizedTitle = React.useMemo(
        () => normalizeUrlPreviewText(getUrlDisplayTitle(typeof currentMetaData?.title === 'string' ? currentMetaData.title : '')),
        [lnT, currentMetaData?.title]
    );
    const normalizedDescription = React.useMemo(
        () => normalizeUrlPreviewText(typeof currentMetaData?.description === 'string' ? currentMetaData.description : ''),
        [lnT, currentMetaData?.description]
    );
    const hasNormalizedTitle = showTitle && normalizedTitle.length > 0;
    const hasNormalizedDescription = showDescription && normalizedDescription.length > 0;
    const hasFooterMeta = Boolean(
        currentMetaData?.quoted_url_meta ||
        currentMetaData?.logo ||
        currentMetaData?.publisher ||
        currentMetaData?.author ||
        currentMetaData?.url
    );
    const titleUsesMarkdown = hasUrlPreviewMarkdownFormatting(normalizedTitle);
    const descriptionUsesMarkdown = hasUrlPreviewMarkdownFormatting(normalizedDescription);
    const shouldCollapseDescription =
        normalizedDescription.length > URL_PREVIEW_DESCRIPTION_COLLAPSE_LIMIT;
    const displayedTitle = truncateUrlPreviewText(normalizedTitle, URL_PREVIEW_TITLE_COLLAPSE_LIMIT);
    const displayedDescription = truncateUrlPreviewText(
        normalizedDescription,
        URL_PREVIEW_DESCRIPTION_COLLAPSE_LIMIT
    );
    const descriptionSearchMatchExcerpt = React.useMemo(() => {
        if (!shouldCollapseDescription) {
            return null;
        }

        return buildSearchMatchPreviewExcerpt(normalizedDescription, searchHighlightTerm, {
            maxLength: URL_META_DESCRIPTION_EXCERPT_LENGTH,
            visibleWindow: URL_META_DESCRIPTION_VISIBLE_WINDOW
        });
    }, [lnT, normalizedDescription, searchHighlightTerm, shouldCollapseDescription]);
    const incomingMediaIdentity = React.useMemo(
        () => buildMetaMediaIdentity(metaData),
        [lnT, metaData]
    );
    const videoWorkspaceSource = React.useMemo(() => {
        const sourceMeta = activeMediaData || currentMetaData;
        if (!sourceMeta) {
            return null;
        }

        const playableVideoSource =
            activeVideoSource ||
            getUrlMetaDirectS3VideoSource(currentMetaData) ||
            getUrlMetaDirectS3VideoSource(activeMediaData) ||
            getUrlMetaOriginalVideoUrl(activeMediaData) ||
            getUrlMetaOriginalVideoUrl(currentMetaData) ||
            activeMediaData?.media_url ||
            currentMetaData?.media_url ||
            '';

        return {
            type: 'video',
            gallery: hasGalleryItems ? workspaceMediaGallery : undefined,
            initialIndex: currentGalleryIndex,
            workspaceUrlMetaIndex: index,
            src: playableVideoSource,
            hlsUrl: activeS3VideoSource ? '' : activeMediaData?.media_hls_url || currentMetaData?.media_hls_url || '',
            originalUrl: currentMetaData?.url || originalVideoSource || '',
            embedUrl: currentMetaData?.embed || '',
            url: currentMetaData?.url || ''
        };
    }, [lnT, activeMediaData, activeVideoSource, activeS3VideoSource, currentMetaData, originalVideoSource, hasGalleryItems, workspaceMediaGallery, currentGalleryIndex, index]);
    const hasPlayableVideoWorkspaceSource = Boolean(
        activeVideoSource ||
        originalVideoSource ||
        currentMetaData?.media_url ||
        activeMediaData?.media_url ||
        activeMediaData?.media_hls_url ||
        currentMetaData?.media_hls_url ||
        currentMetaData?.embed ||
        isYouTubeUrl(currentMetaData?.url || currentMetaData?.media_url) ||
        getYouTubeVideoId(currentMetaData?.url || currentMetaData?.media_url)
    );
    const openTranscriptWorkspace = React.useCallback(() => {
        if (typeof onOpenVideoWorkspace !== 'function') {
            return;
        }

        onOpenVideoWorkspace(videoWorkspaceSource || {
            src: '',
            hlsUrl: '',
            originalUrl: currentMetaData?.url || '',
            embedUrl: currentMetaData?.embed || '',
            url: currentMetaData?.url || ''
        });
    }, [lnT, currentMetaData, onOpenVideoWorkspace, videoWorkspaceSource]);

    const [refetchMessage] = useLazyQuery(GET_MESSAGE, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            const refreshedMetaData = data?.getMessage?.urlMeta?.[index] || null;
            setIsRefreshingMedia(false);

            if (refreshedMetaData) {
                setResolvedMetaData(refreshedMetaData);
                setHasMediaLoaded(false);
                setHasMediaError(false);
                return;
            }

            setHasMediaLoaded(true);
            setHasMediaError(true);
        },
        onError: error => {
            console.error('[URLPreviewSection] Failed to refetch preview image URLs:', error);
            setIsRefreshingMedia(false);
            setHasMediaLoaded(true);
            setHasMediaError(true);
        }
    });

    useEffect(() => {
        const indexChanged = previousIndexRef.current !== index;
        const mediaIdentityChanged = previousMediaIdentityRef.current !== incomingMediaIdentity;

        setResolvedMetaData(metaData);

        // Avoid resetting preview load state when Apollo gives the same media data with new object refs
        // (common on pagination/fetchMore). Reset only when actual media identity changes.
        if (indexChanged || mediaIdentityChanged) {
            setCurrentMediaSrc(null);
            setHasMediaLoaded(false);
            setHasMediaError(false);
            setIsRefreshingMedia(false);
            setHasTriedMediaRefetch(false);
            setCurrentGalleryIndex(0);
        }

        previousIndexRef.current = index;
        previousMediaIdentityRef.current = incomingMediaIdentity;
    }, [metaData, index, incomingMediaIdentity]);

    // Setup ResizeObserver
    useEffect(() => {
        let rafId = null;
        let isObserving = true;
        const STACK_LAYOUT_HYSTERESIS_PX = 16;
        
        const checkWidth = (observedWidth) => {
            if (!isObserving) return;
            if (wrapperRef.current) {
                const measuredWidth =
                    typeof observedWidth === 'number' && Number.isFinite(observedWidth)
                        ? observedWidth
                        : wrapperRef.current.getBoundingClientRect().width;
                const width = Number.isFinite(measuredWidth) ? measuredWidth : 0;
                lastMeasuredWidthRef.current = width;
                setMeasuredWidth(prevWidth => (prevWidth === width ? prevWidth : width));

                setIsSmallScreen(prevIsSmallScreen => {
                    if (!Number.isFinite(width) || width <= 0) {
                        return prevIsSmallScreen;
                    }

                    // Use hysteresis to prevent row/column oscillation around the breakpoint.
                    const stackThreshold = stackLayoutMaxWidth;
                    const compactEnterThreshold = stackThreshold - STACK_LAYOUT_HYSTERESIS_PX;
                    const compactExitThreshold = stackThreshold + STACK_LAYOUT_HYSTERESIS_PX;

                    if (prevIsSmallScreen) {
                        return width <= compactExitThreshold;
                    }

                    if (width >= compactEnterThreshold) {
                        return false;
                    }

                    return true;
                });
            }
        };

        const throttledCheckWidth = observedWidth => {
            if (rafId || !isObserving) return;
            
            rafId = window.requestAnimationFrame(() => {
                checkWidth(observedWidth);
                rafId = null;
            });
        };

        resizeObserverRef.current = new ResizeObserver(entries => {
            const entry = Array.isArray(entries) && entries.length > 0 ? entries[0] : null;
            const entryWidth = Number(entry?.contentRect?.width || 0);
            throttledCheckWidth(entryWidth);
        });
        
        if (wrapperRef.current) {
            resizeObserverRef.current.observe(wrapperRef.current);
            checkWidth();
        }

        // Listen for drag start/end
        const handleDragStart = () => {
            isObserving = false;
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
        };

        const handleDragEnd = () => {
            isObserving = true;
            if (resizeObserverRef.current && wrapperRef.current) {
                resizeObserverRef.current.observe(wrapperRef.current);
                checkWidth(lastMeasuredWidthRef.current || undefined);
            }
        };

        document.addEventListener('dragstart', handleDragStart);
        document.addEventListener('dragend', handleDragEnd);

        return () => {
            isObserving = false;
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
            if (rafId) {
                window.cancelAnimationFrame(rafId);
            }
            document.removeEventListener('dragstart', handleDragStart);
            document.removeEventListener('dragend', handleDragEnd);
        };
    }, [stackLayoutMaxWidth]);

    // Calculate dimensions
    // Uses the width tracked by the ResizeObserver above (only updates on genuine size
    // changes, and re-renders when it does) instead of a live getBoundingClientRect()
    // read, which would re-measure - and can visibly resize the preview - on every
    // incidental re-render (e.g. hover state toggling in a sibling changing the row's
    // flex layout by a few pixels).
    const previewMedia = React.useMemo(
        () => resolveUrlMetaPreviewMedia(currentMetaData),
        [lnT, currentMetaData]
    );
    const {
        directImageUrl,
        mainSources: mainImageSources,
        authorSources: authorImageSources,
        sourceChain: previewSourceChain,
        primarySource: primaryImageSource,
        isAuthorImage
    } = previewMedia;
    const isDirectImageUrl = Boolean(directImageUrl);
    const activeGalleryImage = hasGalleryItems ? activeGallerySlide?.image || null : null;
    const privateImageSource = activeGalleryImage || currentMediaSrc || primaryImageSource;
    const currentImageSourceChain = React.useMemo(() => {
        if (!activeGalleryImage && !currentMediaSrc) {
            return previewSourceChain;
        }

        return getUniqueMediaSources([
            currentMediaSrc,
            activeGalleryImage,
            primaryImageSource
        ]);
    }, [lnT, activeGalleryImage, currentMediaSrc, previewSourceChain, primaryImageSource]);
    const publicImage = useImageRecovery({ sources: readOnly ? (activeGalleryImage
        ? [activeGalleryImage, ...(String(activeGallerySlide?.type).toLowerCase() === 'video' ? [] :
            [activeGallerySlide?.media?.cdn_url, activeGallerySlide?.media?.url])]
        : previewSourceChain) : [], allowRefresh: false });
    const currentImageSource = readOnly ? publicImage.src : privateImageSource;
    latestImageSourceRef.current = normalizeMediaUrl(currentImageSource);


    const getImageDimensions = () => {
        if (!wrapperRef.current) return { width: 200, height: 150 };

        const containerWidth = measuredWidth > 0
            ? measuredWidth
            : wrapperRef.current.getBoundingClientRect().width;
        let maxWidth = mediaMaxWidth; // Default width
        
        // Only resize if container is too small
        if (isSmallScreen && containerWidth < maxWidth + 32) { // 32px for padding/margins
            maxWidth = Math.max(containerWidth - 16, 80); // Account for padding and avoid negative width
        }

        const stableFrameWidth = Number(
            stableGalleryFrameSlide?.width || stableGalleryFrameSlide?.media_width || 0
        );
        const stableFrameHeight = Number(
            stableGalleryFrameSlide?.height || stableGalleryFrameSlide?.media_height || 0
        );
        const stableFrameAspectRatio = stableFrameWidth > 0 && stableFrameHeight > 0
            ? stableFrameHeight / stableFrameWidth
            : Number(stableGalleryFrameSlide?.aspect_ratio || 0);

        // Prefer explicit video dimensions when available
        if (hasGalleryItems && stableFrameWidth > 0 && stableFrameHeight > 0) {
            let videoWidth = maxWidth;
            let videoHeight = Math.floor(maxWidth * stableFrameAspectRatio);
            if (videoHeight > 350) {
                videoHeight = 350;
                videoWidth = Math.max(80, Math.round(350 / stableFrameAspectRatio));
            }
            return {
                width: videoWidth,
                height: videoHeight
            };
        }

        const mediaSourceData = (isUrlMetaVideo(activeMediaData) ? activeMediaData : null) ||
            (isUrlMetaVideo(currentMetaData) ? currentMetaData : null) ||
            (activeMediaData?.media_width && activeMediaData?.media_height ? activeMediaData : null) ||
            (currentMetaData?.media_width && currentMetaData?.media_height ? currentMetaData : null);
        if (mediaSourceData?.media_width && mediaSourceData?.media_height) {
            const videoAspectRatio = mediaSourceData.media_height / mediaSourceData.media_width;
            let videoWidth = maxWidth;
            let videoHeight = Math.floor(maxWidth * videoAspectRatio);
            if (videoHeight > 350) {
                videoHeight = 350;
                videoWidth = Math.max(80, Math.round(350 / videoAspectRatio));
            }
            return {
                width: videoWidth,
                height: videoHeight
            };
        }

        // Fallback to stored aspect_ratio for images
        const loadedAspectRatio = loadedImageGeometry?.source === normalizeMediaUrl(currentImageSource)
            ? loadedImageGeometry.aspectRatio
            : 0;
        const resolvedAspectRatio =
            (hasGalleryItems ? stableFrameAspectRatio : loadedAspectRatio) ||
            activeGallerySlide?.aspect_ratio ||
            currentMetaData?.aspect_ratio ||
            storedAspectRatio;
        const height = resolvedAspectRatio ? Math.floor(maxWidth * resolvedAspectRatio) : maxWidth;
        
        return {
            width: maxWidth,
            height: Math.min(height, 350) // Cap height at 350px
        };
    };

    const { width: imageWidth, height: imageHeight } = getImageDimensions();

    // super annoying on the message itself, only showing the plugin
    const metaActive = !readOnly && holdingKeys && (keys.Meta || keys.Control) && keys.Shift;

    const deleteUrl = async () => {
        await message.urlMeta.splice(index, 1, {});
        updateURL(message, message.urlMeta);
    };

    const deleteUrlImage = () => {
        message.urlMeta[index].image = null;
        updateURL(message, message.urlMeta);
    };

    const deletePublisher = () => {
        message.urlMeta[index].publisher = null;
        updateURL(message, message.urlMeta);
    };

    const deleteTitle = () => {
        message.urlMeta[index].title = null;
        updateURL(message, message.urlMeta);
    };

    const deleteDescription = () => {
        message.urlMeta[index].description = null;
        updateURL(message, message.urlMeta);
    };

    const removeButton = callback => {
        if (metaActive) {
            return (
                <div onClick={callback} style={{ cursor: 'pointer' }}>
                    <IoIosClose color={PRIMARY_COLOR} size={16}></IoIosClose>
                </div>
            );
        }
        return null;
    };

    const removeSection = () => {
        if (metaActive) {
            return (
                <div style={{ marginTop: 4, marginLeft: 16 }} onClick={deleteUrl}>{lnT('messages:removePreview')}</div>
            );
        }
        return null;
    };

    const isCurrentImageEvent = React.useCallback(source => {
        const latestSource = latestImageSourceRef.current;
        const eventSource = normalizeMediaUrl(source);

        return !eventSource || !latestSource || eventSource === latestSource;
    }, [lnT]);

    const handleMediaError = failedSource => {
        if (!isCurrentImageEvent(failedSource)) {
            return;
        }
        if (readOnly) { publicImage.onError(); return; }

        setHasMediaLoaded(false);

        if (isDirectImageUrl) {
            setHasMediaLoaded(true);
            setHasMediaError(true);
            return;
        }

        // Bunny CDN URLs share the same token — if one fails, all size variants will too.
        // Skip the fallback chain and refetch immediately to get a fresh token.
        const failedUrl = String(failedSource || currentImageSource || '');
        const isBunnyUrl = failedUrl.includes('.b-cdn.net') || failedUrl.includes('bunny.net');
        if (isBunnyUrl && !hasTriedMediaRefetch && message?.id && !isRefreshingMedia) {
            console.warn('[URLPreviewSection] Bunny CDN image failed, refetching message for fresh token:', failedUrl);
            setIsRefreshingMedia(true);
            setHasTriedMediaRefetch(true);
            refetchMessage({
                variables: {
                    data: {
                        id: message.id
                    }
                }
            });
            return;
        }

        const nextSource = getNextMediaSource(failedSource || currentImageSource, currentImageSourceChain);

        if (nextSource && normalizeMediaUrl(nextSource) !== normalizeMediaUrl(currentMediaSrc || currentImageSource)) {
            setCurrentMediaSrc(nextSource);
            return;
        }

        if (!hasTriedMediaRefetch && message?.id && !isRefreshingMedia) {
            setIsRefreshingMedia(true);
            setHasTriedMediaRefetch(true);
            refetchMessage({
                variables: {
                    data: {
                        id: message.id
                    }
                }
            });
            return;
        }

        setHasMediaLoaded(true);
        setHasMediaError(true);
    };

    const handleMediaLoad = React.useCallback(() => {
        setHasMediaLoaded(true);
        setHasMediaError(false);
        setHasTriedMediaRefetch(false);
        setIsRefreshingMedia(false);
    }, [lnT]);
    const handleImageLoad = React.useCallback(event => {
        const loadedSource = event?.target?.currentSrc || event?.target?.src;
        if (!isCurrentImageEvent(loadedSource)) {
            return;
        }

        const { naturalWidth, naturalHeight } = event?.target || {};
        if (naturalWidth > 0 && naturalHeight > 0) {
            const source = normalizeMediaUrl(loadedSource);
            const aspectRatio = naturalHeight / naturalWidth;
            setLoadedImageGeometry(previous => (
                previous?.source === source && previous.aspectRatio === aspectRatio
                    ? previous
                    : { source, aspectRatio }
            ));
        }
        handleMediaLoad();
    }, [lnT, handleMediaLoad, isCurrentImageEvent]);
    
    // Use thumbnails based on display width (for S3 images) or full-size URLs (for Cloudinary images)
    const mainImageUrl = mainImageSources[0] || null;
    const authorImageUrl = authorImageSources[0] || null; // Author images are typically 100x100px
    const displayImage = currentImageSource;
    // A YouTube URL is playable by the workspace player even when extraction only
    // supplied a poster thumbnail instead of a direct media URL.
    const isYouTube = Boolean(
        isYouTubeUrl(currentMetaData?.url || currentMetaData?.media_url) ||
        getYouTubeVideoId(currentMetaData?.url || currentMetaData?.media_url)
    );
    const isPlayerSupportedMetaUrl = Boolean(
        isYouTube ||
        (currentMetaData?.url && typeof ReactPlayer.canPlay === 'function' && ReactPlayer.canPlay(currentMetaData.url)) ||
        (currentMetaData?.media_url && typeof ReactPlayer.canPlay === 'function' && ReactPlayer.canPlay(currentMetaData.media_url))
    );
    const hasVideoPreview = isUrlMetaVideo(activeMediaData) || isUrlMetaVideo(currentMetaData) || isPlayerSupportedMetaUrl;
    const showPosterOnlyGalleryPreview = hasVideoPreview && hasGalleryItems;
    const hasMainActions = hasArticleBody;
    const hasImagePreview = !!(isDirectImageUrl || displayImage);
    const renderedImageSource = isDirectImageUrl ? currentMetaData?.url : currentImageSource;
    const renderedImageKey = normalizeMediaUrl(renderedImageSource) || String(renderedImageSource || '');
    const authorImageSize = currentMetaData?.publisher === 'X' ? 48 : 100;
    const mediaWidth = isAuthorImage ? authorImageSize : imageWidth;
    const mediaHeight = isAuthorImage ? authorImageSize : imageHeight;
    const mediaAspectRatio = mediaWidth > 0 ? (mediaHeight / mediaWidth) : 1;
    const videoMediaAspectRatio = isYouTube
        ? (9 / 16)
        : mediaAspectRatio;
    const carouselItems = React.useMemo(() => {
        if (gallerySlides.length > 0) {
            return gallerySlides.map((slide, galleryIndex) => ({
                ...currentMetaData,
                image: slide.image || currentMetaData.image,
                gallery_images: slide.image ? [slide.image] : [],
                image_cdn_url: null,
                thumbnail: null,
                media_cdn_url: slide.type === 'video' ? slide.media_cdn_url : null,
                media_url: slide.type === 'video' ? slide.media_url : null,
                media_hls_url: slide.type === 'video' ? slide.media_hls_url : null,
                media_type: slide.type,
                media_width: slide.width,
                media_height: slide.height,
                media_duration: slide.media_duration,
                media_has_audio: slide.media_has_audio,
                media: slide.media || null,
                ocrText: slide.media?.ocrText || null,
                ocrExtracted: slide.media?.ocrExtracted === true,
                public_id: null,
                gallery_item_index: galleryIndex
            }));
        }

        return [currentMetaData];
    }, [lnT, currentMetaData, gallerySlides]);
    const isStarredMessage = Boolean(message?.isStarred);
    const previewTitleColor = isStarredMessage ? PRIMARY_COLOR : WHITE;
    const previewDescriptionColor = isStarredMessage ? PRIMARY_COLOR_4 : 'rgba(255, 255, 255, 0.72)';
    const previewMutedColor = isStarredMessage ? PRIMARY_COLOR_4 : 'rgba(255, 255, 255, 0.6)';
    const previewLinkColor = isStarredMessage ? PRIMARY_COLOR : FILE_BLUE;
    const previewCodeBackground = isStarredMessage ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)';
    const markdownBlockResetStyle = React.useMemo(
        () => ({
            margin: 0,
            fontSize: 'inherit',
            lineHeight: 'inherit',
            color: 'inherit',
            whiteSpace: 'pre-wrap',
            wordBreak: 'normal',
            overflowWrap: 'anywhere'
        }),
        [lnT]
    );
    const titleMarkdownOptions = React.useMemo(
        () => ({
            disableParsingRawHTML: true,
            overrides: {
                p: { props: { style: markdownBlockResetStyle } },
                strong: { props: { style: { fontWeight: 700, color: previewTitleColor } } },
                em: { props: { style: { fontStyle: 'italic' } } },
                h1: { props: { style: { ...markdownBlockResetStyle, fontWeight: 700 } } },
                h2: { props: { style: { ...markdownBlockResetStyle, fontWeight: 700 } } },
                h3: { props: { style: { ...markdownBlockResetStyle, fontWeight: 700 } } },
                h4: { props: { style: { ...markdownBlockResetStyle, fontWeight: 700 } } },
                h5: { props: { style: { ...markdownBlockResetStyle, fontWeight: 700 } } },
                h6: { props: { style: { ...markdownBlockResetStyle, fontWeight: 700 } } },
                ul: { props: { style: { ...markdownBlockResetStyle, paddingLeft: '16px', listStyleType: 'disc' } } },
                ol: { props: { style: { ...markdownBlockResetStyle, paddingLeft: '16px', listStyleType: 'decimal' } } },
                li: { props: { style: markdownBlockResetStyle } },
                blockquote: {
                    props: {
                        style: {
                            ...markdownBlockResetStyle,
                            borderLeft: `3px solid ${previewLinkColor}`,
                            paddingLeft: '8px'
                        }
                    }
                },
                code: {
                    props: {
                        style: {
                            backgroundColor: previewCodeBackground,
                            padding: '1px 4px',
                            borderRadius: '3px',
                            fontFamily: 'monospace',
                            fontSize: '0.95em',
                            lineHeight: 'inherit',
                            color: previewTitleColor
                        }
                    }
                },
                a: {
                    component: 'span',
                    props: {
                        onClick: e => {
                            e.stopPropagation();
                            const text = e.target.innerText;
                            if (text.startsWith('#')) {
                                setSearchTerm(text);
                                setSearchActive(true);
                            } else if (text.startsWith('@')) {
                                const username = text.substring(1);
                                setSelectedUsername(username);
                                setShowSocialModal(true);
                            } else if (e.target.href) {
                                window.open(e.target.href, '_blank');
                            }
                        },
                        style: {
                            color: previewLinkColor,
                            textDecoration: 'none',
                            cursor: 'pointer',
                            wordBreak: 'normal',
                            overflowWrap: 'anywhere',
                            maxWidth: '100%',
                            display: 'inline',
                            whiteSpace: 'normal',
                            fontWeight: 600
                        }
                    }
                }
            }
        }),
        [lnT,
            markdownBlockResetStyle,
            previewCodeBackground,
            previewLinkColor,
            previewTitleColor,
            setSearchTerm,
            setSelectedUsername,
            setSearchActive,
            setShowSocialModal
        ]
    );
    const descriptionMarkdownOptions = React.useMemo(
        () => ({
            disableParsingRawHTML: true,
            overrides: {
                p: {
                    props: {
                        style: {
                            ...markdownBlockResetStyle,
                            marginBottom: '8px',
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: previewDescriptionColor
                        }
                    }
                },
                strong: { props: { style: { fontWeight: 'bold', fontSize: '14px', lineHeight: '20px', color: previewTitleColor } } },
                em: { props: { style: { fontStyle: 'italic', fontSize: '14px', lineHeight: '20px', color: previewDescriptionColor } } },
                a: {
                    component: 'span',
                    props: {
                        onClick: e => {
                            e.stopPropagation();
                            const text = e.target.innerText;
                            if (text.startsWith('#')) {
                                setSearchTerm(text);
                                setSearchActive(true);
                            } else if (text.startsWith('@')) {
                                const username = text.substring(1);
                                setSelectedUsername(username);
                                setShowSocialModal(true);
                            } else if (e.target.href) {
                                window.open(e.target.href, '_blank');
                            }
                        },
                        style: {
                            color: previewLinkColor,
                            textDecoration: 'none',
                            cursor: 'pointer',
                            wordBreak: 'normal',
                            overflowWrap: 'anywhere',
                            maxWidth: '100%',
                            display: 'inline',
                            whiteSpace: 'normal',
                            fontSize: '14px',
                            lineHeight: '20px'
                        }
                    }
                },
                h1: { props: { style: { ...markdownBlockResetStyle, fontWeight: 'bold', margin: '4px 0 2px 0', fontSize: '14px', lineHeight: '20px', color: previewTitleColor } } },
                h2: { props: { style: { ...markdownBlockResetStyle, fontWeight: 'bold', margin: '3px 0 2px 0', fontSize: '14px', lineHeight: '20px', color: previewTitleColor } } },
                h3: { props: { style: { ...markdownBlockResetStyle, fontWeight: 'bold', margin: '2px 0 1px 0', fontSize: '14px', lineHeight: '20px', color: previewTitleColor } } },
                h4: { props: { style: { ...markdownBlockResetStyle, fontWeight: 'bold', margin: '2px 0 1px 0', fontSize: '14px', lineHeight: '20px', color: previewTitleColor } } },
                h5: { props: { style: { ...markdownBlockResetStyle, fontWeight: 'bold', margin: '2px 0 1px 0', fontSize: '14px', lineHeight: '20px', color: previewTitleColor } } },
                h6: { props: { style: { ...markdownBlockResetStyle, fontWeight: 'bold', margin: '2px 0 1px 0', fontSize: '14px', lineHeight: '20px', color: previewTitleColor } } },
                ul: { props: { style: { ...markdownBlockResetStyle, margin: '4px 0', paddingLeft: '16px', fontSize: '14px', lineHeight: '20px', listStyleType: 'disc', color: previewDescriptionColor } } },
                ol: { props: { style: { ...markdownBlockResetStyle, margin: '4px 0', paddingLeft: '16px', fontSize: '14px', lineHeight: '20px', listStyleType: 'decimal', color: previewDescriptionColor } } },
                li: { props: { style: { ...markdownBlockResetStyle, margin: '2px 0', fontSize: '14px', lineHeight: '20px', display: 'list-item', color: previewDescriptionColor } } },
                blockquote: { props: { style: { ...markdownBlockResetStyle, borderLeft: `3px solid ${previewLinkColor}`, paddingLeft: '8px', margin: '4px 0', fontStyle: 'italic', fontSize: '14px', lineHeight: '20px', color: previewDescriptionColor } } },
                code: { props: { style: { backgroundColor: previewCodeBackground, padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '14px', lineHeight: '20px', color: previewTitleColor } } }
            }
        }),
        [lnT,
            markdownBlockResetStyle,
            previewCodeBackground,
            previewDescriptionColor,
            previewLinkColor,
            previewTitleColor,
            setSearchTerm,
            setSelectedUsername,
            setSearchActive,
            setShowSocialModal
        ]
    );

    useEffect(() => {
        if (gallerySlides.length > 0) {
            const boundedIndex = Math.max(0, Math.min(currentGalleryIndex, gallerySlides.length - 1));
            const selectedGalleryImage =
                gallerySlides[boundedIndex]?.image ||
                gallerySlides[0]?.image ||
                null;
            setCurrentMediaSrc(selectedGalleryImage);
            return;
        }

        setCurrentMediaSrc(primaryImageSource || null);
    }, [primaryImageSource, gallerySlides, currentGalleryIndex]);

    useEffect(() => {
        if (!hasGalleryItems || typeof window === 'undefined') {
            return;
        }

        gallerySlides.forEach(slide => {
            const imageUrl = slide?.image;
            if (typeof imageUrl !== 'string' || imageUrl.trim().length === 0) {
                return;
            }

            const preloadedImage = new window.Image();
            preloadedImage.referrerPolicy = 'no-referrer';
            preloadedImage.src = imageUrl;
        });
    }, [gallerySlides, hasGalleryItems]);

    useEffect(() => {
        if (gallerySlides.length === 0) {
            if (currentGalleryIndex !== 0) {
                setCurrentGalleryIndex(0);
            }
            return;
        }

        if (currentGalleryIndex >= gallerySlides.length) {
            setCurrentGalleryIndex(0);
        }
    }, [gallerySlides.length, currentGalleryIndex]);

    const effectiveGalleryIndex = React.useMemo(() => {
        if (gallerySlides.length === 0) {
            return 0;
        }

        return Math.max(0, Math.min(currentGalleryIndex, gallerySlides.length - 1));
    }, [lnT, gallerySlides.length, currentGalleryIndex]);

    const imageWorkspaceSource = React.useMemo(() => {
        if (!hasImagePreview || !renderedImageSource) {
            return null;
        }

        const galleryImage = gallerySlides[effectiveGalleryIndex]?.image;
        const imagePayload = galleryImage
            ? { url: galleryImage, cdn_url: galleryImage }
            : {
                url: renderedImageSource,
                cdn_url: renderedImageSource,
                displayName: currentMetaData?.title || message?.title || 'URL preview image'
            };

        return {
            type: 'image',
            initialIndex: effectiveGalleryIndex,
            gallery: hasGalleryItems ? workspaceMediaGallery : undefined,
            image: imagePayload,
            url: renderedImageSource,
            originalUrl: currentMetaData?.image ||
                currentMetaData?.image_cdn_url ||
                currentMetaData?.url ||
                renderedImageSource
        };
    }, [lnT,
        currentMetaData?.image,
        currentMetaData?.image_cdn_url,
        currentMetaData?.title,
        currentMetaData?.url,
        effectiveGalleryIndex,
        gallerySlides,
        hasGalleryItems,
        workspaceMediaGallery,
        hasImagePreview,
        message?.title,
        renderedImageSource
    ]);

    const selectGalleryIndex = React.useCallback(nextIndex => {
        if (gallerySlides.length <= 1) {
            return;
        }

        const boundedIndex = Math.max(0, Math.min(nextIndex, gallerySlides.length - 1));
        setCurrentGalleryIndex(previousIndex => {
            if (previousIndex === boundedIndex) {
                return previousIndex;
            }

            setHasMediaError(false);
            setHasTriedMediaRefetch(false);
            return boundedIndex;
        });
    }, [lnT, gallerySlides.length]);

    const renderGalleryPositionBadge = React.useCallback(() => {
        if (!hasGalleryItems) {
            return null;
        }

        return (
            <GalleryPositionBadge aria-hidden="true">
                {lnT('messages:text_7b652f58', { value1: String(effectiveGalleryIndex + 1), value2: String(gallerySlides.length) })}
            </GalleryPositionBadge>
        );
    }, [lnT, effectiveGalleryIndex, gallerySlides.length, hasGalleryItems]);

    useEffect(() => {
        if (!hasGalleryItems) {
            setHasMediaLoaded(false);
        }
        setHasMediaError(false);

        const expectedSource = normalizeMediaUrl(renderedImageSource);
        if (!expectedSource) {
            return undefined;
        }

        let rafId = null;
        let attempts = 0;
        const maxAttempts = 30;

        const checkCachedImage = () => {
            const node = previewImageRef.current;
            const nodeSource = normalizeMediaUrl(node?.currentSrc || node?.src);

            if (node?.complete && node.naturalWidth > 0 && nodeSource === expectedSource) {
                handleImageLoad({ target: node });
                return;
            }

            attempts += 1;
            if (attempts < maxAttempts && latestImageSourceRef.current === normalizeMediaUrl(currentImageSource)) {
                rafId = window.requestAnimationFrame(checkCachedImage);
            }
        };

        rafId = window.requestAnimationFrame(checkCachedImage);

        return () => {
            if (rafId) {
                window.cancelAnimationFrame(rafId);
            }
        };
    }, [currentImageSource, handleImageLoad, hasGalleryItems, hasVideoPreview, renderedImageSource, showPosterOnlyGalleryPreview]);

    const setPreviewImageNode = React.useCallback(node => {
        previewImageRef.current = node;

        // Cached images can already be complete before React's onLoad callback helps us.
        const nodeSource = node?.currentSrc || node?.src;
        if (node?.complete && node.naturalWidth > 0 && isCurrentImageEvent(nodeSource)) {
            handleImageLoad({ target: node });
        }
    }, [lnT, handleImageLoad, isCurrentImageEvent]);

    if (currentMetaData && (hasNormalizedTitle || hasNormalizedDescription || hasArticleBody || displayImage || hasVideoPreview || currentMetaData.author_image || currentMetaData.author_image_original)) {
        return (
            <>
                <UrlWrapper
                    ref={wrapperRef}
                    $isStarred={isStarredMessage}
                    $isMasonry={isMasonryLayout}
                    isSmallScreen={isSmallScreen}
                    style={currentMetaData.quoted_url_meta ? { flexDirection: 'column' } : undefined}
                >
                <div style={{
                    display: 'flex',
                    flexDirection: isSmallScreen ? 'column' : 'row',
                    gap: isSmallScreen ? 8 : 12,
                    width: '100%',
                    minWidth: 0
                }}>
                    {(hasImagePreview || hasVideoPreview) ? (
                        <ImageUrlSection isSmallScreen={isSmallScreen} $isMasonry={isMasonryLayout} $mediaWidth={mediaWidth}>
                            <div
                                data-message-media-interactive="true"
                                style={{
                                    flexGrow: 0,
                                    flexShrink: 0,
                                    flexBasis: isAuthorImage ? 'auto' : `${mediaWidth}px`,
                                    width: isSmallScreen ? '100%' : mediaWidth,
                                    maxWidth: isSmallScreen ? '100%' : mediaWidth,
                                    marginRight: 0,
                                    marginBottom: 0,
                                    cursor: 'pointer'
                                }}
                                onClick={e => {
                                    logVideoWorkspace('URLPreviewSection.media click', {
                                        messageId: message?.id,
                                        hasVideoPreview,
                                        hasHandler: typeof onOpenVideoWorkspace === 'function',
                                        hasSource: Boolean(videoWorkspaceSource)
                                    });

                                    e.stopPropagation();
                                    if (
                                        imageWorkspaceSource &&
                                        typeof onOpenVideoWorkspace === 'function' &&
                                        (!hasVideoPreview || showPosterOnlyGalleryPreview || !hasPlayableVideoWorkspaceSource)
                                    ) {
                                        onOpenVideoWorkspace(imageWorkspaceSource);
                                        return;
                                    }

                                    if (
                                        hasVideoPreview &&
                                        hasPlayableVideoWorkspaceSource &&
                                        typeof onOpenVideoWorkspace === 'function'
                                    ) {
                                        const activeTime = getVideoPlaybackTime(message?.id) || getVideoPlaybackTime(currentMetaData?.url) || 0;
                                        onOpenVideoWorkspace({
                                            ...videoWorkspaceSource,
                                            currentTime: activeTime,
                                            autoPlay: true
                                        });
                                        return;
                                    }

                                    if (imageWorkspaceSource && typeof onOpenVideoWorkspace === 'function') {
                                        onOpenVideoWorkspace(imageWorkspaceSource);
                                        return;
                                    }

                                    setLightboxIsOpen(true);
                                }}
                            >
                                {hasMediaError ? (
                                    <div
                                        style={{
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            minWidth: 150,
                                            minHeight: 100,
                                            backgroundColor: PRIMARY_COLOR_1,
                                            borderRadius: 8,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '8px'
                                        }}
                                    >
                                        <ImageNAIcon />
                                        <div style={{ fontSize: '12px', color: previewMutedColor }}>{lnT('messages:mediaUnavailable')}</div>
                                    </div>
                                ) : hasGalleryItems ? (
                                    <GalleryPreviewGrid aria-label={lnT('messages:mediaGallery')}>
                                        {gallerySlides.map((slide, slideIndex) => {
                                            const isVideoSlide = slide?.type === 'video' || Boolean(getUrlMetaVideoSource(slide));
                                            const slidePoster = slide?.image || '';

                                            return (
                                                <GalleryPreviewItem
                                                    key={`${slidePoster}-${slideIndex}`}
                                                    type="button"
                                                    aria-label={isVideoSlide ? lnT('messages:playVideo_108352ef', { value1: String(slideIndex + 1) }) : lnT('messages:openImage', { value1: String(slideIndex + 1) })}
                                                    onClick={event => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        selectGalleryIndex(slideIndex);

                                                        if (isVideoSlide && typeof onOpenVideoWorkspace === 'function') {
                                                            onOpenVideoWorkspace({
                                                                ...workspaceMediaGallery[slideIndex],
                                                                gallery: workspaceMediaGallery,
                                                                initialIndex: slideIndex
                                                            });
                                                            return;
                                                        }

                                                        if (imageWorkspaceSource && typeof onOpenVideoWorkspace === 'function') {
                                                            onOpenVideoWorkspace({
                                                                ...imageWorkspaceSource,
                                                                initialIndex: slideIndex
                                                            });
                                                            return;
                                                        }

                                                        setLightboxIsOpen(true);
                                                    }}
                                                >
                                                    {slidePoster ? <img src={slidePoster} alt="" referrerPolicy="no-referrer" /> : <PreviewPlaceholder isVisible />}
                                                    {isVideoSlide ? <GalleryVideoPreviewBadge aria-hidden="true">▶</GalleryVideoPreviewBadge> : null}
                                                </GalleryPreviewItem>
                                            );
                                        })}
                                    </GalleryPreviewGrid>
                                ) : hasVideoPreview ? (
                                    <PreviewMediaFrame
                                        isAuthorImage={false}
                                        isCircle={false}
                                        mediaAspectRatio={videoMediaAspectRatio}
                                        mediaWidth={mediaWidth}
                                        mediaHeight={mediaHeight}
                                    >
                                        {renderGalleryPositionBadge()}
                                        <RedditVideoPlayer
                                            messageId={message?.id}
                                            autoplayOnHover={autoplayOnHover}
                                            videoUrl={activeS3VideoSource || originalVideoSource || activeVideoSource || (isPlayerSupportedMetaUrl ? (currentMetaData?.url || currentMetaData?.media_url) : null)}
                                            isYouTube={Boolean(isYouTubeUrl(currentMetaData?.url || currentMetaData?.media_url) || getYouTubeVideoId(currentMetaData?.url || currentMetaData?.media_url))}
                                            hlsUrl={activeS3VideoSource ? null : decodeProviderMediaUrl(activeMediaData.media_hls_url || currentMetaData?.media_hls_url)}
                                            audioUrl={activeS3VideoSource ? null : decodeProviderMediaUrl(activeMediaData.media_audio_url)}
                                            hasAudio={activeMediaData.media_has_audio}
                                            width={mediaWidth}
                                            height={mediaHeight}
                                            poster={currentImageSource || mainImageUrl || activeMediaData.image}
                                            fillFrame
                                            onOpenTranscript={hasTranscriptForUrl && typeof onOpenVideoWorkspace === 'function' && isTranscriptSupportedVideoMeta(activeMediaData) ? openTranscriptWorkspace : null}
                                            onLoad={handleMediaLoad}
                                            onError={handleMediaError}
                                        />
                                    </PreviewMediaFrame>
                                ) : hasImagePreview ? (
                                    <PreviewMediaFrame
                                        isAuthorImage={isAuthorImage}
                                        isCircle={isAuthorImage && currentMetaData.publisher === 'X'}
                                            mediaAspectRatio={mediaAspectRatio}
                                            mediaWidth={mediaWidth}
                                            mediaHeight={mediaHeight}
                                        >
                                            {renderGalleryPositionBadge()}
                                            <PreviewPlaceholder isVisible={!hasMediaLoaded} />
                                            {isDirectImageUrl ? (
                                                <img
                                                    key={renderedImageKey}
                                                    ref={setPreviewImageNode}
                                                    src={currentMetaData.url}
                                                    referrerPolicy="no-referrer"
                                                    alt={currentMetaData.title || lnT('folders:preview')}
                                                    onLoad={handleImageLoad}
                                                    onError={e => handleMediaError(e.target.currentSrc || e.target.src)}
                                                    style={{
                                                        ...PREVIEW_IMAGE_STYLE,
                                                        objectFit: 'contain',
                                                        opacity: hasMediaLoaded ? 1 : 0
                                                    }}
                                                />
                                            ) : isAuthorImage ? (
                                                <img
                                                    key={renderedImageKey}
                                                    ref={setPreviewImageNode}
                                                    src={currentImageSource}
                                                    alt={currentMetaData.author || lnT('messages:author')}
                                                    referrerPolicy="no-referrer"
                                                    onLoad={handleImageLoad}
                                                    onError={e => handleMediaError(e.target.currentSrc || e.target.src)}
                                                    style={{
                                                        ...PREVIEW_IMAGE_STYLE,
                                                        objectFit: 'cover',
                                                        opacity: hasMediaLoaded ? 1 : 0
                                                    }}
                                                />
                                            ) : (currentImageSource && (currentImageSource.includes('cloudinary.com') || (!currentImageSource.includes('http') && !currentImageSource.includes('amazonaws.com') && !currentImageSource.includes('b-cdn.net')))) ? (
                                                <Image
                                                    key={renderedImageKey}
                                                    cloudName={environment.REACT_APP_CLOUD_NAME}
                                                    publicId={currentImageSource}
                                                    width={'1.0'}
                                                    crop={'pad'}
                                                    onLoad={handleImageLoad}
                                                    onError={() => handleMediaError(currentImageSource)}
                                                    style={{
                                                        ...PREVIEW_IMAGE_STYLE,
                                                        objectFit: 'contain',
                                                        opacity: hasMediaLoaded ? 1 : 0
                                                    }}
                                                >
                                                    <Transformation fetchFormat="auto" flags="lossy" quality="50" />
                                                </Image>
                                            ) : (
                                                <img
                                                    key={renderedImageKey}
                                                    ref={setPreviewImageNode}
                                                    src={currentImageSource}
                                                    alt={currentMetaData.title || lnT('folders:preview')}
                                                    referrerPolicy="no-referrer"
                                                    onLoad={handleImageLoad}
                                                    onError={e => handleMediaError(e.target.currentSrc || e.target.src)}
                                                    style={{
                                                        ...PREVIEW_IMAGE_STYLE,
                                                        objectFit: 'contain',
                                                        opacity: hasMediaLoaded ? 1 : 0
                                                    }}
                                                />
                                            )}
                                            {showPosterOnlyGalleryPreview ? (
                                                <GalleryVideoPreviewBadge aria-hidden="true">
                                                    ▶
                                                </GalleryVideoPreviewBadge>
                                            ) : null}
                                        </PreviewMediaFrame>
                                ) : (
                                    <div
                                        style={{
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            minWidth: 150,
                                            minHeight: 100,
                                            backgroundColor: PRIMARY_COLOR_1,
                                            borderRadius: 8,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '8px'
                                        }}
                                    >
                                        <ImageNAIcon />
                                        <div style={{ fontSize: '12px', color: previewMutedColor }}>
                                            {currentMetaData.media_url ? lnT('messages:videoNotSupported') : lnT('messages:noImageAvailable')}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </ImageUrlSection>
                        ) : null}
                        {/* Content section - title first, then description */}
                        {(hasNormalizedDescription || hasNormalizedTitle || hasArticleBody || hasFooterMeta || hasMainActions) ? (
                            <ContentSection isSmallScreen={isSmallScreen} $isMasonry={isMasonryLayout}>
                            <UrlSection>
                                {hasNormalizedTitle ? (
                                    <>
                                        <TitleClamp
                                            style={{
                                                fontWeight: 500,
                                                color: previewTitleColor,
                                                marginBottom: 4,
                                                lineHeight: '20px',
                                                wordBreak: 'normal',
                                                overflowWrap: 'anywhere',
                                                whiteSpace: titleUsesMarkdown ? 'normal' : 'pre-wrap'
                                            }}
                                        >
                                            {titleUsesMarkdown ? (
                                                <Markdown options={titleMarkdownOptions}>
                                                    {displayedTitle}
                                                </Markdown>
                                            ) : renderHighlightedSearchText(displayedTitle, searchHighlightTerm)}
                                        </TitleClamp>
                                    </>
                                ) : null}
                                {hasNormalizedDescription ? (
                                    <>
                                        <DescriptionClamp $hasMainActions={hasMainActions}>
                                        <TruncateWrapper style={{ color: previewDescriptionColor }}>
                                            {descriptionUsesMarkdown ? (
                                                <Markdown options={descriptionMarkdownOptions}>
                                                    {displayedDescription}
                                                </Markdown>
                                            ) : (
                                                <div
                                                    style={{
                                                        margin: 0,
                                                        fontSize: '14px',
                                                        lineHeight: '20px',
                                                        color: previewDescriptionColor,
                                                        wordBreak: 'normal',
                                                        overflowWrap: 'anywhere',
                                                        whiteSpace: 'pre-wrap'
                                                    }}
                                                >
                                                {renderHighlightedSearchText(displayedDescription, searchHighlightTerm)}
                                            </div>
                                        )}
                                        </TruncateWrapper>
                                        </DescriptionClamp>
                                        {descriptionSearchMatchExcerpt ? (
                                            <DescriptionSearchSnippetRow $isStarred={isStarredMessage}>
                                                <DescriptionSearchSnippetLabel $isStarred={isStarredMessage}>{lnT('messages:link')}</DescriptionSearchSnippetLabel>
                                                <DescriptionSearchSnippetText>
                                                    {descriptionSearchMatchExcerpt.hasPrefix ? (
                                                        <SearchMatchExcerptPrefix>&hellip;</SearchMatchExcerptPrefix>
                                                    ) : null}
                                                    {renderHighlightedSearchText(descriptionSearchMatchExcerpt.text, searchHighlightTerm)}
                                                    {descriptionSearchMatchExcerpt.hasSuffix ? (
                                                        <SearchMatchExcerptPrefix>&hellip;</SearchMatchExcerptPrefix>
                                                    ) : null}
                                                </DescriptionSearchSnippetText>
                                            </DescriptionSearchSnippetRow>
                                        ) : null}
                                    </>
                                ) : null}
                            </UrlSection>
                            {hasMainActions ? (
                                <UrlMetaActionStack>
                                    {hasArticleBody ? (
                                        <UrlMetaActionButton
                                            $isLight={isStarredMessage}
                                            type="button"
                                            data-message-interactive="true"
                                            onPointerDown={event => {
                                                event.stopPropagation();
                                            }}
                                            onMouseDown={event => {
                                                event.stopPropagation();
                                            }}
                                            onClick={event => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                openArticleWorkspace();
                                            }}
                                        >
                                            <FiBookOpen size={14} />
                                            <span>{lnT('messages:read')}</span>
                                        </UrlMetaActionButton>
                                    ) : null}
                                </UrlMetaActionStack>
                            ) : null}
                            <div style={{
                                display: 'flex',
                                flexDirection: 'row',
                                marginTop: (hasNormalizedDescription || hasNormalizedTitle) ? 10 : 0,
                                alignItems: 'center',
                                gap: 8,
                                width: '100%',
                                flexWrap: 'wrap',
                                overflow: 'visible',
                                minWidth: 0
                            }}>
                                {currentMetaData.logo ?
                                    <div style={{ 
                                        display: 'flex', 
                                        flexDirection: 'row', 
                                        alignItems: 'center',
                                        flex: 1,
                                        overflow: 'hidden',
                                        minWidth: 0
                                    }}>
                                        <img 
                                            src={currentMetaData.logo}
                                            alt={currentMetaData.publisher}
                                            referrerPolicy="no-referrer"
                                            style={{ 
                                                height: 16, 
                                                width: 16, 
                                                objectFit: 'contain', 
                                                marginRight: 6,
                                                flexShrink: 0
                                            }}
                                        />
                                        <div style={{ 
                                            color: previewMutedColor,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: isSmallScreen ? 'normal' : 'nowrap',
                                            minWidth: 0,
                                            flex: 1
                                        }}>
                                            {currentMetaData.publisher === 'X' ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: isSmallScreen ? 'wrap' : 'nowrap', minWidth: 0 }}>
                                                    <span style={{ 
                                                        fontWeight: 400,
                                                        whiteSpace: isSmallScreen ? 'normal' : 'nowrap',
                                                        wordBreak: 'break-word',
                                                        overflowWrap: 'anywhere'
                                                    }}>
                                                        {currentMetaData.author}
                                                    </span>
                                                    {currentMetaData.author_handle && (
                                                        <span style={{ 
                                                            opacity: 0.7, 
                                                            whiteSpace: isSmallScreen ? 'normal' : 'nowrap',
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'anywhere'
                                                        }}>
                                                            @{currentMetaData.author_handle}
                                                        </span>
                                                    )}
                                                    {currentMetaData.author_verified && (
                                                        <MdVerified size={14} color={PRIMARY_COLOR} style={{ flexShrink: 0 }} />
                                                    )}
                                                </div>
                                            ) : (
                                                currentMetaData.publisher && currentMetaData.publisher.length > 0
                                                    ? renderHighlightedSearchText(currentMetaData.publisher, searchHighlightTerm)
                                                    : domain_from_url(currentMetaData && currentMetaData.url)
                                            )}
                                        </div>
                                    </div>
                                    :
                                    <div style={{ 
                                        color: previewMutedColor,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: isSmallScreen ? 'normal' : 'nowrap',
                                        flex: 1,
                                        minWidth: 0,
                                        maxWidth: '100%'
                                    }}>
                                        {domain_from_url(currentMetaData && currentMetaData.url)}
                                    </div>
                                }
                                {currentMetaData?.sourceMetrics && (currentMetaData.sourceMetrics.viewCount || currentMetaData.sourceMetrics.likeCount || currentMetaData.sourceMetrics.commentCount) ? (
                                    <div style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        fontSize: 11,
                                        color: previewMutedColor,
                                        opacity: 0.9,
                                        flexShrink: 0,
                                        marginLeft: 'auto'
                                    }}>
                                        {currentMetaData.sourceMetrics.viewCount ? (
                                            <span title={`${currentMetaData.sourceMetrics.viewCount.toLocaleString()} views`}>
                                                👁 {formatCompactMetric(currentMetaData.sourceMetrics.viewCount)}
                                            </span>
                                        ) : null}
                                        {currentMetaData.sourceMetrics.likeCount ? (
                                            <span title={`${currentMetaData.sourceMetrics.likeCount.toLocaleString()} likes`}>
                                                ❤️ {formatCompactMetric(currentMetaData.sourceMetrics.likeCount)}
                                            </span>
                                        ) : null}
                                        {currentMetaData.sourceMetrics.commentCount ? (
                                            <span title={`${currentMetaData.sourceMetrics.commentCount.toLocaleString()} comments`}>
                                                💬 {formatCompactMetric(currentMetaData.sourceMetrics.commentCount)}
                                            </span>
                                        ) : null}
                                        {(() => {
                                            const erBadge = getEngagementRateBadge(currentMetaData.sourceMetrics.engagementRate, previewMutedColor);
                                            if (!erBadge) return null;
                                            return (
                                                <span
                                                    style={{
                                                        padding: '1px 5px',
                                                        borderRadius: 4,
                                                        fontSize: 10,
                                                        fontWeight: 600,
                                                        backgroundColor: erBadge.backgroundColor,
                                                        color: erBadge.color
                                                    }}
                                                    title={erBadge.title}
                                                >
                                                    {erBadge.icon ? `${erBadge.icon} ` : ''}
                                                    {erBadge.label}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                ) : null}
                                {readOnly && currentMetaData.url ? (
                                    <SourceOpenLink href={currentMetaData.url} target="_blank" rel="noopener noreferrer"
                                        onClick={event => event.stopPropagation()}>{lnT('common:open')}</SourceOpenLink>
                                ) : null}
                            </div>
                        </ContentSection>
                    ) : null}
                </div>
                {currentMetaData.quoted_url_meta ? (
                    <UrlMetaQuotedPreview
                        metaData={currentMetaData.quoted_url_meta}
                        allowImageRefresh={!readOnly}
                        showOpen={false}
                        variant={isStarredMessage ? 'light' : 'dark'}
                        onReadArticle={openArticleWorkspace}
                        onOpenWorkspace={onOpenVideoWorkspace}
                    />
                ) : null}
                </UrlWrapper>
                <CarouselModal
                    readOnly={readOnly}
                    isOpen={lightboxIsOpen}
                    closeModal={e => {
                        e.stopPropagation();
                        setLightboxIsOpen(false);
                    }}
                    items={carouselItems}
                    fileType="Link"
                    currentMessage={message}
                    messageId={readOnly ? undefined : message?.id}
                    initialIndex={effectiveGalleryIndex}
                    allOcrExtracted={galleryOcrIsComplete}
                />
                <ArticleReaderModal
                    isOpen={isArticleReaderOpen}
                    onRequestClose={() => setIsArticleReaderOpen(false)}
                    title={currentMetaData?.title || message?.title}
                    url={currentMetaData?.article_url || currentMetaData?.url}
                    articleBody={articleBody}
                    redditComments={currentMetaData?.reddit_comments}
                    sourcePlatform={currentMetaData?.sourcePlatform}
                    sourceMetrics={currentMetaData?.sourceMetrics}
                    sourcePlatformMeta={currentMetaData?.sourcePlatformMeta}
                    onCreateAudio={readOnly ? undefined : handleCreateArticleAudio}
                    isCreateAudioDisabled={readOnly || isCreatingArticleAudio}
                    isCreatingAudio={isCreatingArticleAudio}
                />
                <ArticleReaderModal
                    isOpen={Boolean(quotedArticleReaderMeta)}
                    onRequestClose={() => setQuotedArticleReaderMeta(null)}
                    title={quotedArticleReaderMeta?.title}
                    url={quotedArticleReaderMeta?.article_url || quotedArticleReaderMeta?.url}
                    articleBody={quotedArticleReaderMeta?.article_body || ''}
                    redditComments={quotedArticleReaderMeta?.reddit_comments}
                    sourcePlatform={quotedArticleReaderMeta?.sourcePlatform}
                    sourceMetrics={quotedArticleReaderMeta?.sourceMetrics}
                    sourcePlatformMeta={quotedArticleReaderMeta?.sourcePlatformMeta}
                />
                {!readOnly && <TextToSpeechCreateModal
                    isOpen={isArticleAudioModalOpen}
                    onClose={() => setIsArticleAudioModalOpen(false)}
                    message={message}
                    source="ARTICLE"
                    text={articleAudioText}
                    urlMetaIndex={index}
                />}
            </>
        );
    } else if (currentMetaData) {
        return (
            <UrlWrapper ref={wrapperRef} $isStarred={isStarredMessage} $isMasonry={isMasonryLayout} style={{ justifyContent: 'flex-start' }}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        width: '100%'
                    }}
                >
                    {currentMetaData.logo ?
                        <img 
                            src={currentMetaData.logo}
                            alt={currentMetaData.publisher}
                            referrerPolicy="no-referrer"
                            style={{ 
                                height: 32, 
                                width: 32, 
                                objectFit: 'contain', 
                                borderRadius: 8, 
                                marginRight: 6,
                                flexShrink: 0
                            }}
                        />
                        :
                        <div
                            style={{
                                display: 'flex',
                                padding: 8,
                                backgroundColor: FILE_BLUE,
                                borderRadius: 8,
                                marginRight: 6,
                                flexShrink: 0
                            }}
                        >
                            <IoMdLink color={WHITE} size={16} />
                        </div>
                    }
                    <span style={{ 
                        color: previewMutedColor,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                        maxWidth: '100%',
                        flex: 1
                    }}>
                        {domain_from_url(currentMetaData.url)}
                    </span>
                    {readOnly && currentMetaData.url ? (
                        <SourceOpenLink href={currentMetaData.url} target="_blank" rel="noopener noreferrer"
                            onClick={event => event.stopPropagation()}>{lnT('common:open')}</SourceOpenLink>
                    ) : null}
                </div>
            </UrlWrapper>
        );
    }
    return null;
}

const PREVIEW_IMAGE_STYLE = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    borderRadius: 'inherit',
    transition: 'opacity 0.25s ease-in-out',
    display: 'block'
};

const PreviewMediaFrame = styled.div`
    position: relative;
    width: ${props => props.isAuthorImage ? `${props.mediaWidth}px` : '100%'};
    border-radius: ${props => props.isCircle ? '50%' : '8px'};
    overflow: hidden;
    background-color: ${PRIMARY_COLOR_1};
    ${props => props.isAuthorImage ? `
        height: ${props.mediaHeight}px;
    ` : `
        height: 0;
        padding-bottom: ${(props.mediaAspectRatio > 0 ? props.mediaAspectRatio : 1) * 100}%;
    `}
`;

const PreviewPlaceholder = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: ${PRIMARY_COLOR_1};
    opacity: ${props => props.isVisible ? 1 : 0};
    transition: opacity 0.2s ease-in-out;
`;

const GalleryPositionBadge = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 3;
    padding: 3px 7px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.5);
    color: #ffffff;
    font-size: 10px;
    font-weight: 700;
    line-height: 1.2;
    pointer-events: none;
`;

const GalleryVideoPreviewBadge = VideoPlayOverlay;

const TranscriptCornerButton = styled.button`
    position: absolute;
    right: 8px;
    bottom: 8px;
    z-index: 4;
    padding: 4px 6px;
    border: 0;
    border-radius: 5px;
    background: rgba(0, 0, 0, 0.72);
    color: #ffffff;
    font-size: 10px;
    font-weight: 800;
    line-height: 1;
    cursor: pointer;

    &:hover,
    &:focus-visible {
        background: rgba(0, 0, 0, 0.9);
    }
`;

const SourceOpenLink = styled.a`
    color: ${FILE_BLUE};
    flex-shrink: 0;
    margin-left: 12px;
    font-size: 13px;
    text-decoration: none;
    white-space: nowrap;
    &:hover { text-decoration: underline; }
`;

const UrlWrapper = styled.div`
    display: flex;
    // Keep the video on the left by default; stack only when the measured
    // preview width drops below the responsive threshold.
    flex-direction: ${props => props.isSmallScreen ? 'column' : 'row'};
    padding: 8px;
    background-color: ${props => (props.$isStarred ? 'rgba(0, 0, 0, 0.06)' : 'rgba(0, 0, 0, 0.2)')};
    border: 1px solid ${props => (props.$isStarred ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)')};
    border-radius: 8px;
    width: 100%;
    max-width: 100%;
    position: relative;
    gap: ${props => props.isSmallScreen ? '8px' : '0'};
    min-width: 0;
    box-sizing: border-box;
`;

const ImageUrlSection = styled.div`
    display: flex;
    flex-direction: row;
    width: ${props => props.isSmallScreen ? '100%' : 'auto'};
    flex-shrink: 0;
    align-items: flex-start;
    justify-content: ${props => props.isSmallScreen ? 'center' : 'flex-start'};
    overflow: hidden;
    max-width: ${props => {
        if (props.isSmallScreen) return '100%';
        if (props.$mediaWidth) return `${props.$mediaWidth}px`;
        return '240px';
    }};
`;

const ContentSection = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    ${props => props.isSmallScreen && `
        width: 100%;
    `}
    justify-content: center;
`;

const UrlSection = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
    word-break: break-word;
    overflow-wrap: break-word;
`;

const TitleClamp = styled.div`
    display: block;
    overflow: hidden;
`;

const DescriptionClamp = styled.div`
    display: block;
    overflow: hidden;
    max-height: ${props => (props.$hasMainActions ? '40px' : '80px')};
`;

const TruncateWrapper = styled.div`
    display: block;
    color: inherit;
    word-break: break-word;
    overflow-wrap: break-word;
`;

const DescriptionSearchSnippetRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
    margin-top: 6px;
    padding: 5px 7px;
    border-radius: 7px;
    background: ${props => props.$isStarred ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 214, 102, 0.08)'};
    border: 1px solid ${props => props.$isStarred ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 214, 102, 0.2)'};
    color: ${props => props.$isStarred ? PRIMARY_COLOR : 'rgba(255, 255, 255, 0.88)'};
    font-size: 12px;
    line-height: 16px;
`;

const DescriptionSearchSnippetLabel = styled.span`
    flex: 0 0 auto;
    color: ${props => props.$isStarred ? PRIMARY_COLOR_4 : 'rgba(255, 255, 255, 0.58)'};
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.02em;
`;

const DescriptionSearchSnippetText = styled.span`
    min-width: 0;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
`;

const SearchMatchExcerptPrefix = styled.span`
    opacity: 0.5;
`;

const UrlMetaActionStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    margin-top: 10px;

    & > div > button {
        background: transparent;
    }

    & > div > button:hover {
        background: transparent;
    }
`;

const UrlMetaActionButton = UrlPreviewReadButton;

const areUrlPreviewPropsEqual = (previousProps, nextProps) => {
    return (
        previousProps.message?.id === nextProps.message?.id &&
        previousProps.message?.updatedAt === nextProps.message?.updatedAt &&
        previousProps.layoutMode === nextProps.layoutMode &&
        previousProps.showTitle === nextProps.showTitle &&
        previousProps.showDescription === nextProps.showDescription &&
        previousProps.searchHighlightTerm === nextProps.searchHighlightTerm &&
        previousProps.onOpenVideoWorkspace === nextProps.onOpenVideoWorkspace &&
        buildUrlPreviewIdentity(previousProps.metaData) === buildUrlPreviewIdentity(nextProps.metaData)
    );
};

export default React.memo(URLPreviewSection, areUrlPreviewPropsEqual);
